<script setup lang="ts">
import logoVegco from '@/assets/logo-vegco.png'
import { notifyError } from '@/utils/helpers'



/* ── Types ──────────────────────────────────────────────────────────── */

declare interface CTPalette {
    codeProduit: string
    /** Description anglaise CyberTrace, celle attendue sur le GRL. */
    descriptionSupp: string
    noLot: string
    noItem: string
    statut: number
    noClient: string
    nomClient: string
    noFournisseur: string
    nomFournisseur: string
    quantite: number
    quantiteInventaire: number
    paletteId: string
    noCommandeAchat: string
    noCommandeVente: string
    poClient: string
    idEtat: string
    dateReception: string
    dateRecolte: string
    dateExpedition: string
    poidsBrut: number
    poidsContenant: number
    poidsPalette: number
}

declare interface SemisChampsResult {
    siteId: string
    siteNom: string
    semiId: string
    numéro: string
    champId: string
    champNom: string
}

/** Une ligne du document : soit une palette, soit une ligne de sous-total. */
declare interface RangéeGRL {
    clé: string
    type: 'palette' | 'totalFerme' | 'totalProduit'
    /** Rempli seulement sur la première ligne du bloc, comme dans le document d'origine. */
    codeProduit: string
    description: string
    ferme: string
    dateRecolte: string
    noLot: string
    champ: string
    paletteId: string
    libellé: string
    quantite: number
    poidsNet: number
}

/* ── Constantes ─────────────────────────────────────────────────────── */

/** `GET vgco/palettes` est plafonné à `TOP (1000)` côté SQL, sans indication de troncature. */
const LIMITE_API = 1000

/* ── État ───────────────────────────────────────────────────────────── */

const theme = useTheme()
const route = useRoute()
const router = useRouter()

const isLoading = ref(false)
/** Saisie en cours, distincte de la commande réellement chargée. */
const saisieCommande = ref('')
const commandeChargée = ref('')
const palettes = ref<CTPalette[]>([])
const champsParLot = ref<Record<string, string>>({})

/* ── Formatage ──────────────────────────────────────────────────────── */

const nfEntier = new Intl.NumberFormat('fr-CA', { maximumFractionDigits: 0, useGrouping: false })
const nfPoids = new Intl.NumberFormat('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false })

function fmtQuantité(n: number): string {
    return nfEntier.format(n ?? 0)
}

function fmtPoids(n: number): string {
    return nfPoids.format(n ?? 0)
}

/** Format du modèle papier : dd/MM/yyyy, malgré les entêtes en anglais. */
function fmtDateDoc(valeur: string): string {
    const d = new Date(valeur)
    if (Number.isNaN(d.getTime()) || d.getFullYear() <= 2000)
        return ''
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
}

function poidsNet(p: CTPalette): number {
    return Math.max(0, (p.poidsBrut || 0) - (p.poidsPalette || 0) - (p.quantite || 0) * (p.poidsContenant || 0))
}

/* ── Chargement ─────────────────────────────────────────────────────── */

let génération = 0

async function chargerChamps(noLots: string[]) {
    champsParLot.value = {}
    if (noLots.length === 0)
        return

    // ASP.NET Core ne lie les tableaux qu'à partir de clés répétées (`?semiNoms=a&semiNoms=b`).
    const params = new URLSearchParams()
    for (const lot of noLots)
        params.append('semiNoms', lot)

    const réponse = await apiProphet.get('vgco/semis/champs', { params })
    const table: Record<string, string> = {}
    for (const r of réponse.data as SemisChampsResult[])
        if (r.numéro && r.champNom)
            table[r.numéro] = r.champNom
    champsParLot.value = table
}

async function getData() {
    const commande = saisieCommande.value.trim()
    if (!commande) {
        palettes.value = []
        champsParLot.value = {}
        commandeChargée.value = ''
        return
    }

    const gen = ++génération
    try {
        isLoading.value = true

        const params = new URLSearchParams()
        params.append('noCommandesVente', commande)

        const réponse = await apiProphet.get('vgco/palettes', { params })
        if (gen !== génération)
            return

        const lignes = réponse.data as CTPalette[]
        palettes.value = lignes
        commandeChargée.value = commande

        const noLots = [...new Set(lignes.map(p => p.noLot).filter(Boolean))]
        await chargerChamps(noLots)
        if (gen !== génération)
            return

        if (lignes.length === 0)
            Notify.error(`Aucune palette rattachée au bon de vente ${commande}.`)
    }
    catch (e) {
        if (gen === génération) {
            palettes.value = []
            champsParLot.value = {}
        }
        notifyError(e)
    }
    finally {
        if (gen === génération)
            isLoading.value = false
    }
}

/* ── Entête du document ─────────────────────────────────────────────── */

const client = computed(() => {
    const noms = palettes.value.map(p => p.nomClient?.trim()).filter(Boolean) as string[]
    if (noms.length === 0)
        return ''
    // Une commande peut porter plusieurs livraisons : on retient le destinataire majoritaire.
    const comptes = new Map<string, number>()
    for (const nom of noms)
        comptes.set(nom, (comptes.get(nom) ?? 0) + 1)
    return [...comptes.entries()].sort((a, b) => b[1] - a[1])[0]![0]
})

const dateExpédition = computed(() => {
    let max = 0
    for (const p of palettes.value) {
        const t = new Date(p.dateExpedition).getTime()
        if (!Number.isNaN(t) && new Date(t).getFullYear() > 2000 && t > max)
            max = t
    }
    return max > 0 ? fmtDateDoc(new Date(max).toISOString()) : ''
})

/* ── Construction du document ───────────────────────────────────────── */

/**
 * Le document reprend la hiérarchie du modèle papier : produit → ferme → semis.
 * Les cellules d'un même bloc ne sont écrites que sur sa première ligne, le reste est laissé
 * vide (le regroupement se lit alors comme sur le PDF d'origine).
 */
const rangées = computed((): RangéeGRL[] => {
    const sortie: RangéeGRL[] = []

    const parProduit = new Map<string, CTPalette[]>()
    for (const p of palettes.value) {
        const clé = p.codeProduit || '—'
        const liste = parProduit.get(clé)
        if (liste)
            liste.push(p)
        else
            parProduit.set(clé, [p])
    }

    const codes = [...parProduit.keys()].sort((a, b) => a.localeCompare(b, 'fr-CA'))

    for (const code of codes) {
        const produits = parProduit.get(code)!
        const description = produits.find(p => p.descriptionSupp?.trim())?.descriptionSupp.trim() ?? ''

        const parFerme = new Map<string, CTPalette[]>()
        for (const p of produits) {
            const clé = p.nomFournisseur?.trim() || p.noFournisseur || '—'
            const liste = parFerme.get(clé)
            if (liste)
                liste.push(p)
            else
                parFerme.set(clé, [p])
        }

        const fermes = [...parFerme.keys()].sort((a, b) => a.localeCompare(b, 'fr-CA'))
        let premièreLigneProduit = true
        let quantitéProduit = 0
        let poidsProduit = 0

        for (const ferme of fermes) {
            const palettesFerme = parFerme.get(ferme)!

            const parSemis = new Map<string, CTPalette[]>()
            for (const p of palettesFerme) {
                const clé = `${p.noLot || '—'}\u0000${p.dateRecolte}`
                const liste = parSemis.get(clé)
                if (liste)
                    liste.push(p)
                else
                    parSemis.set(clé, [p])
            }

            const clésSemis = [...parSemis.keys()].sort((a, b) => a.localeCompare(b, 'fr-CA'))
            let premièreLigneFerme = true
            let quantitéFerme = 0
            let poidsFerme = 0

            for (const cléSemis of clésSemis) {
                const palettesSemis = parSemis.get(cléSemis)!
                    .slice()
                    .sort((a, b) => a.paletteId.localeCompare(b.paletteId, 'fr-CA'))
                const [noLot] = cléSemis.split('\u0000')
                let premièreLigneSemis = true

                for (const p of palettesSemis) {
                    const net = poidsNet(p)
                    quantitéFerme += p.quantite || 0
                    poidsFerme += net
                    quantitéProduit += p.quantite || 0
                    poidsProduit += net

                    sortie.push({
                        clé: `${code}|${ferme}|${cléSemis}|${p.paletteId}|${sortie.length}`,
                        type: 'palette',
                        codeProduit: premièreLigneProduit ? code : '',
                        description: premièreLigneProduit ? description : '',
                        ferme: premièreLigneFerme ? ferme : '',
                        dateRecolte: premièreLigneSemis ? fmtDateDoc(p.dateRecolte) : '',
                        noLot: premièreLigneSemis ? (noLot === '—' ? '' : noLot!) : '',
                        champ: premièreLigneSemis ? (champsParLot.value[noLot ?? ''] ?? '') : '',
                        paletteId: p.paletteId,
                        libellé: '',
                        quantite: p.quantite || 0,
                        poidsNet: net,
                    })

                    premièreLigneProduit = false
                    premièreLigneFerme = false
                    premièreLigneSemis = false
                }
            }

            sortie.push({
                clé: `total-ferme|${code}|${ferme}`,
                type: 'totalFerme',
                codeProduit: '', description: '', ferme: '', dateRecolte: '', noLot: '', champ: '', paletteId: '',
                libellé: 'Farm total',
                quantite: quantitéFerme,
                poidsNet: poidsFerme,
            })
        }

        sortie.push({
            clé: `total-produit|${code}`,
            type: 'totalProduit',
            codeProduit: '', description: '', ferme: '', dateRecolte: '', noLot: '', champ: '', paletteId: '',
            libellé: `Total for ${code}`,
            quantite: quantitéProduit,
            poidsNet: poidsProduit,
        })
    }

    return sortie
})

const totaux = computed(() => ({
    palettes: palettes.value.length,
    quantite: palettes.value.reduce((s, p) => s + (p.quantite || 0), 0),
    poidsNet: palettes.value.reduce((s, p) => s + poidsNet(p), 0),
    produits: new Set(palettes.value.map(p => p.codeProduit).filter(Boolean)).size,
    fermes: new Set(palettes.value.map(p => p.nomFournisseur).filter(Boolean)).size,
    semis: new Set(palettes.value.map(p => p.noLot).filter(Boolean)).size,
}))

/** Semis présents sur la commande mais introuvables côté Prophète : la colonne Field reste vide. */
const lotsSansChamp = computed(() =>
    [...new Set(palettes.value.map(p => p.noLot).filter(Boolean))]
        .filter(lot => !champsParLot.value[lot]))

const limiteAtteinte = computed(() => palettes.value.length >= LIMITE_API)

/* ── Impression ─────────────────────────────────────────────────────── */

async function imprimer() {
    // Le rendu papier dépend du DOM courant : on laisse Vue le remettre à jour avant d'ouvrir la boîte.
    await nextTick()
    window.print()
}

/* ── Export PDF (pdfmake) ───────────────────────────────────────────── */

/** Largeurs en points; `*` absorbe le reste de la largeur utile (712 pt en lettre paysage). */
const LARGEURS_PDF: (number | string)[] = [120, '*', 72, 84, 72, 96, 58, 74]

const ENTETES_PDF = [
    'Product Code', 'Farm name', 'Harvest Date', 'Seedling Number',
    'Field', 'Serial number', 'Quantity', 'Net Weight lbs',
]

const NOIR = '#000000'
const GRIS_FILET = '#d0d0d0'

let logoDataUrl: string | null = null

/** pdfmake n'accepte que des images en data URL : l'asset n'est lu qu'au premier export. */
async function logoPourPdf(): Promise<string> {
    if (logoDataUrl)
        return logoDataUrl
    const blob = await (await fetch(logoVegco)).blob()
    logoDataUrl = await new Promise<string>((resolve, reject) => {
        const lecteur = new FileReader()
        lecteur.onload = () => resolve(lecteur.result as string)
        lecteur.onerror = () => reject(lecteur.error)
        lecteur.readAsDataURL(blob)
    })
    return logoDataUrl
}

async function chargerPdfMake() {
    const [modulePdf, moduleVfs] = await Promise.all([
        import('pdfmake/build/pdfmake'),
        import('pdfmake/build/vfs_fonts'),
    ])
    // Bundle UMD : selon l'interop, l'API se trouve à la racine du module ou sous `default`.
    const pdfMake = ('createPdf' in modulePdf ? modulePdf : (modulePdf as any).default) as typeof modulePdf
    pdfMake.addVirtualFileSystem((moduleVfs as any).default ?? moduleVfs)
    // pdfmake 0.3 n'enregistre plus Roboto d'office : sans ça, `provideFont` lève une exception.
    pdfMake.addFonts({
        Roboto: {
            normal: 'Roboto-Regular.ttf',
            bold: 'Roboto-Medium.ttf',
            italics: 'Roboto-Italic.ttf',
            bolditalics: 'Roboto-MediumItalic.ttf',
        },
    })
    return pdfMake
}

function celluleTexte(texte: string, alignement: 'left' | 'center' | 'right', filet: boolean) {
    return {
        text: texte,
        alignment: alignement,
        border: [false, false, false, filet],
        borderColor: [GRIS_FILET, GRIS_FILET, GRIS_FILET, GRIS_FILET],
    }
}

function corpsTableauPdf() {
    const entete = ENTETES_PDF.map((titre, i) => ({
        text: titre,
        bold: true,
        alignment: i <= 1 || i === 5 ? 'left' : i >= 6 ? 'right' : 'center',
        border: [false, false, false, true],
        borderColor: [NOIR, NOIR, NOIR, NOIR],
    }))

    const corps: any[][] = [entete]

    for (const r of rangées.value) {
        if (r.type === 'palette') {
            corps.push([
                {
                    // Le code et sa description ne sont écrits que sur la première ligne du bloc produit.
                    stack: r.codeProduit
                        ? [{ text: r.codeProduit, bold: true }, { text: r.description }]
                        : [{ text: '' }],
                    border: [false, false, false, false],
                },
                { text: r.ferme, border: [false, false, false, false] },
                celluleTexte(r.dateRecolte, 'center', false),
                celluleTexte(r.noLot, 'center', false),
                celluleTexte(r.champ, 'center', false),
                celluleTexte(r.paletteId, 'left', true),
                celluleTexte(fmtQuantité(r.quantite), 'right', true),
                celluleTexte(fmtPoids(r.poidsNet), 'right', true),
            ])
            continue
        }

        const produit = r.type === 'totalProduit'
        const bordure = [false, true, false, produit]
        const couleur = [NOIR, NOIR, NOIR, NOIR]
        // La cellule fusionnée couvre 5 colonnes suivantes; pdfmake exige quand même 8 cellules par ligne.
        const vides = Array.from({ length: 4 }, () => ({}))

        corps.push([
            produit
                ? { text: r.libellé, bold: true, colSpan: 6, border: bordure, borderColor: couleur }
                : { text: '', border: [false, false, false, false] },
            produit ? {} : { text: r.libellé, bold: true, colSpan: 5, border: bordure, borderColor: couleur },
            ...vides,
            { text: fmtQuantité(r.quantite), bold: true, alignment: 'right', border: bordure, borderColor: couleur },
            { text: fmtPoids(r.poidsNet), bold: true, alignment: 'right', border: bordure, borderColor: couleur },
        ])
    }

    return corps
}

async function télécharger() {
    if (rangées.value.length === 0)
        return

    try {
        isLoading.value = true
        const [pdfMake, logo] = await Promise.all([chargerPdfMake(), logoPourPdf()])
        const commande = commandeChargée.value

        await pdfMake.createPdf({
            pageSize: 'LETTER',
            pageOrientation: 'landscape',
            pageMargins: [40, 40, 40, 46],
            defaultStyle: { font: 'Roboto', fontSize: 8 },
            info: { title: `GRL ${commande}`, subject: 'Grower Ranch Lot' },
            content: [
                {
                    columns: [
                        {
                            stack: [
                                { text: 'GRL - Grower Ranch Lot', fontSize: 20 },
                                { text: `Order #${commande}`, fontSize: 10, bold: true, margin: [0, 10, 0, 2] },
                                { text: `Client: ${client.value || '—'}` },
                                { text: `Shipping date: ${dateExpédition.value || '—'}` },
                            ],
                        },
                        { image: logo, width: 100, alignment: 'right' },
                    ],
                    margin: [0, 0, 0, 16],
                },
                {
                    table: {
                        widths: LARGEURS_PDF,
                        // L'entête se répète en haut de chaque page, comme sur le modèle.
                        headerRows: 1,
                        dontBreakRows: true,
                        body: corpsTableauPdf(),
                    },
                    layout: {
                        defaultBorder: false,
                        hLineWidth: (i: number) => (i === 1 ? 1 : 0.6),
                        vLineWidth: () => 0,
                        paddingLeft: (i: number) => (i === 0 ? 0 : 4),
                        paddingRight: (i: number) => (i === ENTETES_PDF.length - 1 ? 0 : 4),
                        paddingTop: () => 3,
                        paddingBottom: () => 3,
                    },
                },
            ],
            footer: (page: number, total: number) => ({
                columns: [
                    { image: logo, width: 44, margin: [40, 0, 0, 0] },
                    { text: `Order #${commande}`, fontSize: 8, margin: [8, 4, 0, 0] },
                    { text: `${page} / ${total}`, fontSize: 8, alignment: 'right', margin: [0, 4, 40, 0] },
                ],
            }),
        } as any).download(`GRL-${commande}.pdf`)
    }
    catch (e) {
        notifyError(e)
    }
    finally {
        isLoading.value = false
    }
}

/* ── Cycle de vie ───────────────────────────────────────────────────── */

watch(commandeChargée, (valeur) => {
    router.replace({ query: valeur ? { commande: valeur } : {} })
})

onMounted(() => {
    const commande = route.query.commande
    if (typeof commande === 'string' && commande.trim()) {
        saisieCommande.value = commande.trim()
        getData()
    }
})
</script>

<template>
    <v-card class="mx-4 my-4 page-card" rounded="xl" :loading="isLoading"
        style="height: 90vh; display: flex; flex-direction: column;">

        <!-- Page header -->
        <div class="page-header px-5 pt-5 pb-3">
            <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">

                <div class="d-flex align-center" style="gap: 12px;">
                    <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
                        <v-icon icon="M6,2A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2H6M6,4H13V9H18V20H6V4M8,12V14H16V12H8M8,16V18H13V16H8Z" size="26"/>
                    </v-avatar>
                    <div>
                        <div class="text-h6 font-weight-bold" style="line-height: 1.2;">GRL — Grower Ranch Lot</div>
                        <div class="text-caption text-medium-emphasis d-none d-sm-block">
                            Traçabilité d'un bon de vente : produits, fermes, semis, champs et palettes
                        </div>
                    </div>
                    <v-divider vertical class="mx-2 d-none d-sm-block" style="height: 32px; align-self: center;"/>
                    <v-text-field v-model="saisieCommande" label="No de commande (bon de vente)" density="compact"
                        variant="outlined" rounded="lg" hide-details clearable autocomplete="off"
                        prepend-inner-icon="M19,3H14.82C14.25,1.44 12.53,0.64 11,1.2C10.14,1.5 9.5,2.16 9.18,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M12,3A1,1 0 0,1 13,4A1,1 0 0,1 12,5A1,1 0 0,1 11,4A1,1 0 0,1 12,3M7,7H17V5H19V19H5V5H7V7M17,11H7V9H17V11M15,15H7V13H15V15Z" style="min-width: 230px; max-width: 260px;"
                        @keyup.enter="getData"/>
                    <v-btn color="primary" variant="tonal" rounded="pill" prepend-icon="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"
                        :disabled="!saisieCommande.trim()" @click="getData">
                        <span class="d-none d-sm-inline">Générer</span>
                    </v-btn>
                </div>

                <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                    <v-btn color="primary" variant="flat" rounded="pill" prepend-icon="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3M9.5 11.5C9.5 12.3 8.8 13 8 13H7V15H5.5V9H8C8.8 9 9.5 9.7 9.5 10.5V11.5M14.5 13.5C14.5 14.3 13.8 15 13 15H10.5V9H13C13.8 9 14.5 9.7 14.5 10.5V13.5M18.5 10.5H17V11.5H18.5V13H17V15H15.5V9H18.5V10.5M12 10.5H13V13.5H12V10.5M7 10.5H8V11.5H7V10.5Z"
                        :disabled="rangées.length === 0" @click="télécharger">
                        <span class="d-none d-sm-inline">Télécharger PDF</span>
                    </v-btn>
                    <v-btn variant="text" rounded="pill" prepend-icon="M18,3H6V7H18M19,12A1,1 0 0,1 18,11A1,1 0 0,1 19,10A1,1 0 0,1 20,11A1,1 0 0,1 19,12M16,19H8V14H16M19,8H5A3,3 0 0,0 2,11V17H6V21H18V17H22V11A3,3 0 0,0 19,8Z"
                        :disabled="rangées.length === 0" @click="imprimer">
                        <span class="d-none d-sm-inline">Imprimer</span>
                    </v-btn>
                </div>
            </div>

            <!-- Totaux de contrôle : écran seulement, le document papier a ses propres sous-totaux -->
            <div v-if="palettes.length > 0" class="d-flex align-center flex-wrap mt-3" style="gap: 8px;">
                <v-chip size="small" variant="tonal" prepend-icon="mdi-pallet">
                    {{ fmtQuantité(totaux.palettes) }} palettes
                </v-chip>
                <v-chip size="small" variant="tonal" prepend-icon="M21,16.5C21,16.88 20.79,17.21 20.47,17.38L12.57,21.82C12.41,21.94 12.21,22 12,22C11.79,22 11.59,21.94 11.43,21.82L3.53,17.38C3.21,17.21 3,16.88 3,16.5V7.5C3,7.12 3.21,6.79 3.53,6.62L11.43,2.18C11.59,2.06 11.79,2 12,2C12.21,2 12.41,2.06 12.57,2.18L20.47,6.62C20.79,6.79 21,7.12 21,7.5V16.5M12,4.15L10.11,5.22L16,8.61L17.96,7.5L12,4.15M6.04,7.5L12,10.85L13.96,9.75L8.08,6.35L6.04,7.5M5,15.91L11,19.29V12.58L5,9.21V15.91M19,15.91V9.21L13,12.58V19.29L19,15.91Z">
                    {{ fmtQuantité(totaux.quantite) }} unités
                </v-chip>
                <v-chip size="small" variant="tonal" prepend-icon="M12,3A4,4 0 0,1 16,7C16,7.73 15.81,8.41 15.46,9H18C18.95,9 19.75,9.67 19.95,10.56C21.96,18.57 22,18.78 22,19A2,2 0 0,1 20,21H4A2,2 0 0,1 2,19C2,18.78 2.04,18.57 4.05,10.56C4.25,9.67 5.05,9 6,9H8.54C8.19,8.41 8,7.73 8,7A4,4 0 0,1 12,3M12,5A2,2 0 0,0 10,7A2,2 0 0,0 12,9A2,2 0 0,0 14,7A2,2 0 0,0 12,5Z">
                    {{ fmtPoids(totaux.poidsNet) }} lb net
                </v-chip>
                <v-chip size="small" variant="tonal" prepend-icon="M2,6H4V18H2V6M5,6H6V18H5V6M7,6H10V18H7V6M11,6H12V18H11V6M14,6H16V18H14V6M17,6H20V18H17V6M21,6H22V18H21V6Z">
                    {{ totaux.produits }} produits
                </v-chip>
                <v-chip size="small" variant="tonal" prepend-icon="M5,4V11.26C3.2,11.9 2,13.6 2,15.5C2,18 4,20 6.5,20C8.79,20 10.71,18.28 10.97,16H15.17C15.06,16.32 15,16.66 15,17A3,3 0 0,0 18,20A3,3 0 0,0 21,17C21,16.66 20.94,16.32 20.82,16H22V13C22,11.89 21.11,11 20,11H15.04L13.65,4H5M7,6H12L13,11V14H10.74C10.16,12.38 8.71,11.23 7,11.03V6M6.5,13.25A2.25,2.25 0 0,1 8.75,15.5A2.25,2.25 0 0,1 6.5,17.75A2.25,2.25 0 0,1 4.25,15.5A2.25,2.25 0 0,1 6.5,13.25M18,15.5A1.5,1.5 0 0,1 19.5,17A1.5,1.5 0 0,1 18,18.5A1.5,1.5 0 0,1 16.5,17A1.5,1.5 0 0,1 18,15.5Z">
                    {{ totaux.fermes }} fermes
                </v-chip>
                <v-chip size="small" variant="tonal" prepend-icon="M2,22V20C2,20 7,18 12,18C17,18 22,20 22,20V22H2M11.3,9.1C10.1,5.2 4,6.1 4,6.1C4,6.1 4.2,13.9 9.9,12.7C9.5,9.8 8,9 8,9C10.8,9 11,12.4 11,12.4V17C11.3,17 11.7,17 12,17C12.3,17 12.7,17 13,17V12.8C13,12.8 13,8.9 16,7.9C16,7.9 14,10.9 14,12.9C21,13.6 21,4 21,4C21,4 12.1,3 11.3,9.1Z">
                    {{ totaux.semis }} semis
                </v-chip>
            </div>
        </div>

        <v-divider/>

        <!-- Aperçu du document -->
        <div class="zone-apercu" style="flex: 1 1 auto; overflow: auto;">

            <v-alert v-if="limiteAtteinte" type="warning" variant="tonal" density="compact"
                class="mx-4 mt-4 alerte-ecran" icon="M13 14H11V9H13M13 18H11V16H13M1 21H23L12 2L1 21Z">
                {{ LIMITE_API }} palettes retournées — l'API plafonne le résultat, le document est peut-être incomplet.
            </v-alert>

            <v-alert v-if="lotsSansChamp.length > 0" type="info" variant="tonal" density="compact"
                class="mx-4 mt-4 alerte-ecran" icon="M12,2C8.14,2 5,5.14 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9C19,5.14 15.86,2 12,2M12.88,15.75H11.13V14H12.88M12.88,12.88H11.13C11.13,10.04 13.75,10.26 13.75,8.5A1.75,1.75 0 0,0 12,6.75A1.75,1.75 0 0,0 10.25,8.5H8.5A3.5,3.5 0 0,1 12,5A3.5,3.5 0 0,1 15.5,8.5C15.5,10.69 12.88,10.91 12.88,12.88Z">
                Aucun champ trouvé pour {{ lotsSansChamp.length }} semis&nbsp;: {{ lotsSansChamp.join(', ') }}
            </v-alert>

            <div v-if="rangées.length === 0 && !isLoading" class="d-flex flex-column align-center justify-center pa-12
                text-medium-emphasis alerte-ecran">
                <v-icon icon="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H13C12.59,21.75 12.2,21.44 11.86,21.1C11.53,20.77 11.25,20.4 11,20H6V4H13V9H18V10.18C18.71,10.34 19.39,10.61 20,11V8L14,2M20.31,18.9C21.64,16.79 21,14 18.91,12.68C16.8,11.35 14,12 12.69,14.08C11.35,16.19 12,18.97 14.09,20.3C15.55,21.23 17.41,21.23 18.88,20.32L22,23.39L23.39,22L20.31,18.9M16.5,19A2.5,2.5 0 0,1 14,16.5A2.5,2.5 0 0,1 16.5,14A2.5,2.5 0 0,1 19,16.5A2.5,2.5 0 0,1 16.5,19Z" size="48" class="mb-3"/>
                <div class="text-body-2">Saisissez un numéro de bon de vente pour générer le document.</div>
            </div>

            <div v-else class="feuille-hote">
                <div class="feuille">

                    <div class="doc-entete">
                        <div>
                            <div class="doc-titre">GRL - Grower Ranch Lot</div>
                            <div class="doc-commande">Order #{{ commandeChargée }}</div>
                            <div class="doc-meta">Client: {{ client || '—' }}</div>
                            <div class="doc-meta">Shipping date: {{ dateExpédition || '—' }}</div>
                        </div>
                        <img :src="logoVegco" alt="Vegco" class="doc-logo">
                    </div>

                    <table class="doc-tableau">
                        <thead>
                            <tr>
                                <th class="col-produit">Product Code</th>
                                <th class="col-ferme">Farm name</th>
                                <th class="col-date">Harvest Date</th>
                                <th class="col-semis">Seedling Number</th>
                                <th class="col-champ">Field</th>
                                <th class="col-serie">Serial number</th>
                                <th class="col-qte">Quantity</th>
                                <th class="col-poids">Net Weight lbs</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="r in rangées" :key="r.clé" :class="`rangee-${r.type}`">
                                <template v-if="r.type === 'palette'">
                                    <td class="col-produit">
                                        <template v-if="r.codeProduit">
                                            <div class="produit-code">{{ r.codeProduit }}</div>
                                            <div class="produit-desc">{{ r.description }}</div>
                                        </template>
                                    </td>
                                    <td class="col-ferme">{{ r.ferme }}</td>
                                    <td class="col-date">{{ r.dateRecolte }}</td>
                                    <td class="col-semis">{{ r.noLot }}</td>
                                    <td class="col-champ">{{ r.champ }}</td>
                                    <td class="col-serie">{{ r.paletteId }}</td>
                                    <td class="col-qte">{{ fmtQuantité(r.quantite) }}</td>
                                    <td class="col-poids">{{ fmtPoids(r.poidsNet) }}</td>
                                </template>
                                <template v-else>
                                    <td class="cellule-vide"/>
                                    <td class="cellule-total" colspan="5">{{ r.libellé }}</td>
                                    <td class="col-qte">{{ fmtQuantité(r.quantite) }}</td>
                                    <td class="col-poids">{{ fmtPoids(r.poidsNet) }}</td>
                                </template>
                            </tr>
                        </tbody>
                    </table>

                    <div class="doc-pied">
                        <img :src="logoVegco" alt="Vegco" class="doc-logo doc-logo--pied">
                        <span>Order #{{ commandeChargée }}</span>
                    </div>

                </div>
            </div>
        </div>
    </v-card>
</template>

<style scoped>
/* ── Page header ─────────────────────────── */
.page-header {
    background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}

/* ── Aperçu papier ───────────────────────── */
.zone-apercu {
    background: v-bind('theme.global.name.value === "dark" ? "rgba(0, 0, 0, 0.25)" : "rgba(0, 0, 0, 0.06)"');
}

.feuille-hote {
    display: flex;
    justify-content: center;
    padding: 24px 16px;
}

/* Lettre paysage (11 po) rendue à 96 ppp, marges comprises. */
.feuille {
    width: 1056px;
    min-height: 816px;
    padding: 46px 56px;
    background: #ffffff;
    color: #000000;
    box-shadow: 0 2px 14px rgba(0, 0, 0, 0.28);
    font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
}

.doc-entete {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 22px;
}

.doc-titre {
    font-size: 28px;
    font-weight: 300;
    line-height: 1.1;
}

.doc-commande {
    margin-top: 12px;
    font-size: 14px;
    font-weight: 700;
}

.doc-meta {
    font-size: 11px;
    line-height: 1.35;
}

.doc-logo {
    width: 132px;
    height: auto;
    flex: none;
}

/* ── Tableau du document ─────────────────── */
.doc-tableau {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
}

.doc-tableau th {
    padding: 4px 8px;
    font-weight: 700;
    text-align: center;
    vertical-align: bottom;
    border-bottom: 1px solid #000000;
}

.doc-tableau td {
    padding: 4px 8px;
    vertical-align: top;
}

.col-produit { width: 150px; }
.col-ferme { width: 150px; }
.col-date { width: 105px; text-align: center; }
.col-semis { width: 105px; text-align: center; }
.col-champ { width: 120px; text-align: center; }
.col-serie { width: 130px; }
.col-qte { width: 80px; text-align: end; }
.col-poids { width: 110px; text-align: end; }

.doc-tableau th.col-serie,
.doc-tableau th.col-produit,
.doc-tableau th.col-ferme {
    text-align: start;
}

/* Les numéros de série sont séparés par un filet fin, comme sur le modèle. */
.rangee-palette td.col-serie,
.rangee-palette td.col-qte,
.rangee-palette td.col-poids {
    border-bottom: 1px solid #d0d0d0;
}

.produit-code {
    font-weight: 700;
}

.produit-desc {
    line-height: 1.25;
}

.cellule-total {
    font-weight: 700;
    border-top: 1px solid #000000;
}

.rangee-totalFerme td:not(.cellule-vide),
.rangee-totalProduit td:not(.cellule-vide) {
    font-weight: 700;
    border-top: 1px solid #000000;
}

.rangee-totalProduit .cellule-total {
    font-size: 13px;
}

.rangee-totalProduit td:not(.cellule-vide) {
    border-bottom: 1px solid #000000;
}

.doc-pied {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 28px;
    font-size: 10px;
}

.doc-logo--pied {
    width: 52px;
}

/* ── Impression ──────────────────────────── */
@media print {
    .page-header,
    .alerte-ecran {
        display: none !important;
    }

    .page-card {
        height: auto !important;
        margin: 0 !important;
        border: none !important;
        box-shadow: none !important;
        background: #ffffff !important;
    }

    .zone-apercu {
        overflow: visible !important;
        background: none !important;
    }

    .feuille-hote {
        display: block;
        padding: 0;
    }

    .feuille {
        width: auto;
        min-height: 0;
        padding: 0;
        box-shadow: none;
    }

    /* L'entête de colonnes se répète sur chaque feuille et aucune ligne n'est coupée en deux. */
    .doc-tableau thead {
        display: table-header-group;
    }

    .doc-tableau tr {
        break-inside: avoid;
    }
}
</style>

<!--
    Non `scoped` : à l'impression il faut neutraliser la chrome applicative (barre, tiroir, pied)
    et les conteneurs à hauteur fixe de la mise en page, qui vivent hors de ce composant.
-->
<style>
@media print {
    @page {
        size: letter landscape;
        margin: 12mm;
    }

    .v-application,
    .v-application__wrap,
    .v-layout,
    .v-main {
        display: block !important;
        height: auto !important;
        min-height: 0 !important;
        padding: 0 !important;
        overflow: visible !important;
    }

    .v-app-bar,
    .v-navigation-drawer,
    .v-footer,
    .v-overlay-container {
        display: none !important;
    }
}
</style>
